import { PhoneIcon } from '@heroicons/react/24/solid'
import { getCookie, setCookie } from 'cookies-next'
import Image from 'next/image'
import { useRouter } from 'next/router'
import {
  ComponentProps,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'

import { useAuth } from '@/api/models/auth/hooks/useAuth'
import { useContactInfo } from '@/api/models/common/hooks/useContactInfo'
import { useProjectList } from '@/api/models/main'
import { cn } from '@/app/utils'
import { AppButton } from '@/components/shared/button'
import { AppModal } from '@/components/shared/modal'
import {
  AppRadioGroup,
  RadioGroupOption
} from '@/components/shared/radio-group'
import { formatPhoneNumber } from '@/utils/number'
import { toRedirectPath } from '@/utils/toRedirectPath'

import { ProjectOption } from '../model/project-option'
import {
  SELECTED_PROJECT_COOKIE_KEY,
  useSelectedProjectStore
} from '../model/selected-project.store'
import { ProjectOptionRenderer } from './ProjectOptionRenderer'

type Props = Omit<ComponentProps<typeof AppModal>, 'title'> & {
  notClosable: boolean
}

const AppProjectModal: FC<Props> = props => {
  const { data: contactInfo } = useContactInfo()
  const hasContactPhone = Boolean(contactInfo?.contactPhone)

  const { dataWithoutParkingPlaces: projectList } = useProjectList()
  const { logout } = useAuth()

  const router = useRouter()

  const { selectedProjectId, setSelectedProjectId } = useSelectedProjectStore()

  const isUserHaveProjects = Boolean(projectList?.length)

  const projectOptions = useMemo(
    () =>
      projectList?.map(project => ({
        key: project.bc_guid,
        name: project.bc_name,
        objects: project.premises,
        personalAccounts: project.personalAccounts,
        src: project.src
      })) ?? [],
    [projectList]
  )

  const isOnlyOneProject = projectOptions?.length === 1

  const [selectedProjectOption, setSelectedProjectOption] =
    useState<ProjectOption>()

  useEffect(() => {
    if (selectedProjectId) {
      setSelectedProjectOption(
        projectOptions.find(
          (project: ProjectOption) => project.key === selectedProjectId
        )
      )
      return
    }

    if (isOnlyOneProject) {
      setSelectedProjectOption(projectOptions[0])
    }
  }, [projectOptions, selectedProjectId, isOnlyOneProject])

  const selectProject = useCallback(
    (project?: ProjectOption) => {
      const selected = project ?? selectedProjectOption
      if (isUserHaveProjects && selected) {
        setCookie(SELECTED_PROJECT_COOKIE_KEY, selected.key)
        setSelectedProjectId(selected.key)
        props.onClose()

        const path = toRedirectPath(router.query?.redirect)
        router.replace(path ?? '/')
      } else {
        logout()
      }
    },
    [
      isUserHaveProjects,
      logout,
      props,
      router,
      selectedProjectOption,
      setSelectedProjectId
    ]
  )

  // если у пользователя только 1 доступный проект происходит выбор этого проекта
  useEffect(() => {
    if (
      !getCookie(SELECTED_PROJECT_COOKIE_KEY) &&
      selectedProjectOption &&
      isOnlyOneProject
    ) {
      selectProject()
    }
  }, [isOnlyOneProject, selectedProjectOption, selectProject])

  const handleChangeProject = useCallback(
    (project: RadioGroupOption) => {
      const projectOption = project as ProjectOption
      setSelectedProjectOption(projectOption)

      selectProject(projectOption)
    },
    [selectProject]
  )

  const handleOptionClick = useCallback(
    (project: RadioGroupOption) => {
      if (selectedProjectId && project.key === selectedProjectId) {
        props.onClose()
      }
    },
    [selectedProjectId, props]
  )

  if (!isUserHaveProjects) {
    return (
      <AppModal
        className="max-w-[460px]"
        overlayClassName="!bg-black/50"
        contentClassName="flex flex-col items-center gap-12 mt-12 pb-32"
        header="Нет доступа"
        onClose={logout}
        opened={props.opened}
      >
        <Image
          src="/images/shared/Blocked.png"
          width={140}
          height={140}
          alt="blocked"
        />
        <p className="font-bold text-20">У вас нет доступа к проектам</p>{' '}
        <p className="text-center text-grey-70 text-20">
          Если у вас есть вопросы свяжитесь со Службой комфорта Sminex
        </p>
        {hasContactPhone && (
          <AppButton
            variant="secondary"
            fluid
            className="!h-56 gap-8"
            onClick={() =>
              (window.location.href = `tel:${contactInfo?.contactPhone}`)
            }
          >
            <div className="flex h-32 w-32 items-center justify-center gap-8 rounded-full bg-bronze-40">
              <PhoneIcon className="h-18 w-18" />
            </div>
            {formatPhoneNumber(contactInfo?.contactPhone)}
          </AppButton>
        )}
      </AppModal>
    )
  }

  return (
    <AppModal
      overlayClassName={cn({ 'backdrop-blur': props.notClosable })}
      className="w-[560px]"
      title="Выберите проект"
      {...props}
    >
      {projectOptions.length > 0 && (
        <AppRadioGroup
          className="mt-16"
          circleClassName="mt-4"
          optionWrapperClassName="items-start w-full"
          nameContainerClassName="grow"
          wrapperClassNames="divide-y divide-grey-30 !gap-0"
          optionClassName="py-24 text-20 first:pt-0 last:pb-0"
          nameRenderer={ProjectOptionRenderer}
          value={selectedProjectOption}
          options={projectOptions}
          onChange={handleChangeProject}
          onOptionClick={handleOptionClick}
        />
      )}
    </AppModal>
  )
}

export default AppProjectModal
